/**
 * Contributions & payouts settings tab (community-speech plan, Phase C).
 *
 * Managers declare an optional rate card (per unit of VALIDATED work,
 * free-text currency — the platform records and computes, it never
 * moves money), review per-contributor totals, and export a payout
 * CSV per period. Amounts everywhere are labeled owner-declared.
 */
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { downloadFile } from "../../utils/download";
import { Button, Checkbox, SettingsCard, useToast } from "@humansignal/ui";
import { Modal } from "../../components/Modal/Modal";
import { Pagination, usePage, usePageSize } from "../../components/Pagination/Pagination";
import { SettingsPageHeader } from "../../components/SettingsPageHeader/SettingsPageHeader";
import { ProjectContext } from "../../providers/ProjectProvider";
import { useTranslation } from "../../i18n/LocaleProvider";
import { cn } from "../../utils/bem";

const getCsrfToken = () => {
  if (typeof document === "undefined") return "";
  const m = document.cookie.match(/(?:^|;\s*)csrftoken=([^;]+)/);
  return m ? decodeURIComponent(m[1]) : "";
};

// Must cover every LedgerEntry kind the backend credits — a kind
// missing here still counts in the row's total (computed server-side
// over ALL rated kinds) but gets no column, so the visible breakdown
// stops adding up to the total.
const KINDS = ["annotation_accepted", "vote_cast", "prompt_accepted", "clip_validated"];

const inputStyle = {
  padding: "8px 10px",
  borderRadius: 8,
  border: "1px solid var(--color-neutral-border, #cbd5e1)",
  fontSize: "var(--font-size-body-small)",
  background: "var(--color-neutral-background, #fff)",
};

export const ContributionsSettings = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const { project } = useContext(ProjectContext);
  const [currency, setCurrency] = useState("");
  const [rates, setRates] = useState({});
  const [visible, setVisible] = useState(true);
  const [excludeManagers, setExcludeManagers] = useState(false);
  const [ledger, setLedger] = useState(null);
  const [saving, setSaving] = useState(false);
  // Rate-change protection: the card as loaded from the server, so a
  // save can tell whether rates actually changed — a change requires
  // the owner to pick a SCOPE and triggers contributor notification
  // plus the accept/exit modal in the labeling flow.
  const initialRef = useRef(null);
  const [scopeOpen, setScopeOpen] = useState(false);
  // Org policies — enforced payment policies render locked here ("Set
  // by your organization"); the server forces/rejects regardless.
  const [orgPolicies, setOrgPolicies] = useState({});
  // Value-LOCK policies pin the setting at the org's value in BOTH
  // directions once enforced (enforced+off = locked off). Gates
  // (future-only) still only act when on.
  const lock = (key) =>
    orgPolicies[key]?.enforced ? { locked: true, value: orgPolicies[key].value === true } : { locked: false };
  const visibleLock = lock("payment.require_visible_rates");
  const excludeLock = lock("payment.exclude_managers");
  const futureOnly =
    orgPolicies["payment.rate_changes_future_only"]?.enforced &&
    orgPolicies["payment.rate_changes_future_only"]?.value === true;

  useEffect(() => {
    if (!project?.id) return;
    fetch(`/api/projects/${project.id}/effective-policies/`, {
      credentials: "same-origin",
      cache: "no-store",
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((body) => setOrgPolicies(body?.policies ?? {}))
      .catch(() => {});
  }, [project?.id]);
  const [ledgerPage, setLedgerPage] = usePage("lp", 1);
  const [ledgerPageSize, setLedgerPageSize] = usePageSize("lp_size", 10);

  const load = useCallback(async () => {
    if (!project?.id) return;
    try {
      const [rc, lg] = await Promise.all([
        fetch(`/api/projects/${project.id}/rate-card/`, {
          credentials: "same-origin",
          cache: "no-store",
        }).then((r) => r.json()),
        fetch(`/api/projects/${project.id}/ledger/`, {
          credentials: "same-origin",
          cache: "no-store",
        }).then((r) => (r.ok ? r.json() : null)),
      ]);
      setCurrency(rc.currency || "");
      initialRef.current = rc.declared ? { currency: rc.currency || "", rates: rc.rates || {} } : null;
      setRates(rc.rates || {});
      setVisible(rc.visible_to_contributors !== false);
      setExcludeManagers(rc.exclude_managers === true);
      setLedger(lg);
    } catch {
      /* transient */
    }
  }, [project?.id]);
  useEffect(() => {
    load();
  }, [load]);

  const ratesChanged = () => {
    const init = initialRef.current;
    if (!init) return false;
    if ((init.currency || "") !== (currency || "")) return true;
    const a = init.rates || {};
    const b = rates || {};
    const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
    for (const k of keys) if (Number(a[k] ?? 0) !== Number(b[k] ?? 0)) return true;
    return false;
  };

  const onSaveClick = () => {
    // An edit to a live card is a contract change — the owner must
    // pick who it applies to; contributors are notified either way.
    if (ratesChanged()) setScopeOpen(true);
    else save("future");
  };

  const save = async (scope) => {
    setSaving(true);
    setScopeOpen(false);
    try {
      const r = await fetch(`/api/projects/${project.id}/rate-card/`, {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", "X-CSRFToken": getCsrfToken() },
        body: JSON.stringify({
          currency,
          rates,
          // Enforced org policies win server-side; mirror them in the payload.
          visible_to_contributors: visibleLock.locked ? visibleLock.value : visible,
          exclude_managers: excludeLock.locked ? excludeLock.value : excludeManagers,
          scope,
        }),
      });
      if (r.ok) {
        toast.show({ message: t("pages.settings.contributions.saved_toast") });
        load();
      } else {
        const b = await r.json().catch(() => ({}));
        toast.show({ type: "error", message: b.detail || `(${r.status})` });
      }
    } finally {
      setSaving(false);
    }
  };

  if (!project?.id) return null;

  return (
    <div className={cn("settings-page").toClassName()}>
      <SettingsPageHeader
        title={t("pages.settings.contributions.title")}
        subtitle={t("pages.settings.contributions.intro")}
      />

      <SettingsCard
        title={t("pages.settings.contributions.rate_card")}
        description={t(
          "pages.settings.contributions.rate_card_help",
          undefined,
          "Owner-declared amounts per unit of validated work. The platform records and computes, it never moves money.",
        )}
      >
        {/* Uniform grid: currency + one cell per work kind, equal
            widths (the old free-flex row gave every input a different
            width and read as clutter). */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))",
            gap: 12,
            marginBottom: 12,
          }}
        >
          <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span style={{ fontSize: "var(--font-size-body-small)", fontWeight: 500 }}>
              {t("pages.settings.contributions.currency")}
            </span>
            <input
              type="text"
              value={currency}
              placeholder="NGN / ETB / airtime…"
              onChange={(e) => setCurrency(e.target.value)}
              style={inputStyle}
            />
          </label>
          {KINDS.map((kind) => (
            <label key={kind} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ fontSize: "var(--font-size-body-small)", fontWeight: 500 }}>
                {t(`pages.settings.contributions.kinds.${kind}`)}
              </span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={rates[kind] ?? ""}
                placeholder="—"
                onChange={(e) =>
                  setRates((prev) => {
                    const next = { ...prev };
                    if (e.target.value === "") delete next[kind];
                    else next[kind] = Number(e.target.value);
                    return next;
                  })
                }
                style={inputStyle}
              />
            </label>
          ))}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
          {/* Stacked (owner-requested): one checkbox per row; Save sits
              level with the column, not above it. */}
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <Checkbox
              checked={visibleLock.locked ? visibleLock.value : visible}
              disabled={visibleLock.locked}
              onChange={(e) => setVisible(e.target.checked)}
            >
              {t("pages.settings.contributions.visible_label")}
            </Checkbox>
            <Checkbox
              checked={excludeLock.locked ? excludeLock.value : excludeManagers}
              disabled={excludeLock.locked}
              onChange={(e) => setExcludeManagers(e.target.checked)}
            >
              {t("pages.settings.contributions.exclude_managers_label", undefined, "Managers don't accrue earnings")}
            </Checkbox>
            {(visibleLock.locked || excludeLock.locked) && (
              <span
                style={{
                  fontSize: "var(--font-size-body-small)",
                  color: "var(--color-neutral-content-subtler, #64748b)",
                }}
              >
                Set by your organization.
              </span>
            )}
          </div>
          <div style={{ marginLeft: "auto" }}>
            <Button
              type="button"
              size="small"
              style={{ width: 120 }}
              waiting={saving}
              onClick={onSaveClick}
              aria-label={t("pages.settings.contributions.save")}
            >
              {t("pages.settings.contributions.save")}
            </Button>
          </div>
        </div>
      </SettingsCard>

      <SettingsCard
        title={t("pages.settings.contributions.totals")}
        headerActions={
          <Button
            type="button"
            look="outlined"
            size="small"
            onClick={() => {
              void downloadFile(`/api/projects/${project.id}/ledger/?export=csv`, `ledger-project-${project.id}.csv`);
            }}
            aria-label={t("pages.settings.contributions.download_csv")}
          >
            {t("pages.settings.contributions.download_csv")}
          </Button>
        }
      >
        {!ledger || ledger.contributors?.length === 0 ? (
          <p
            style={{
              margin: 0,
              fontSize: "var(--font-size-body-small)",
              color: "var(--color-neutral-content-subtler, #475569)",
            }}
          >
            {t("pages.settings.contributions.empty")}
          </p>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "var(--font-size-body-small)" }}>
              <thead>
                <tr
                  style={{ textAlign: "left", color: "var(--color-neutral-content-subtler, #64748b)", fontWeight: 500 }}
                >
                  <th style={{ padding: "6px 8px", fontWeight: 500 }}>
                    {t("pages.settings.contributions.contributor")}
                  </th>
                  {KINDS.map((k) => (
                    <th key={k} style={{ padding: "6px 8px", fontWeight: 500 }}>
                      {t(`pages.settings.contributions.kinds.${k}`)}
                    </th>
                  ))}
                  <th style={{ padding: "6px 8px", fontWeight: 500 }}>
                    {t("pages.settings.contributions.amount")}
                    {ledger.currency ? ` (${ledger.currency})` : ""}
                  </th>
                </tr>
              </thead>
              <tbody>
                {ledger.contributors.slice((ledgerPage - 1) * ledgerPageSize, ledgerPage * ledgerPageSize).map((c) => (
                  <tr key={c.user_id} style={{ borderTop: "1px solid var(--color-neutral-border-subtle, #e2e8f0)" }}>
                    <td style={{ padding: "6px 8px" }}>{c.name || c.email}</td>
                    {KINDS.map((k) => (
                      <td key={k} style={{ padding: "6px 8px" }}>
                        {c.units[k] || 0}
                      </td>
                    ))}
                    <td style={{ padding: "6px 8px", fontVariantNumeric: "tabular-nums" }}>
                      {c.amount != null ? c.amount : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {ledger?.contributors?.length > 0 && (
          <Pagination
            name="project-ledger"
            page={ledgerPage}
            pageSize={ledgerPageSize}
            totalItems={ledger.contributors.length}
            urlParamName="lp"
            pageSizeOptions={[10, 25, 50, 100]}
            onPageLoad={(p, ps) => {
              setLedgerPage(p);
              setLedgerPageSize(ps);
            }}
            style={{ paddingTop: 12 }}
          />
        )}
      </SettingsCard>

      {scopeOpen && (
        <Modal
          visible
          onHide={() => setScopeOpen(false)}
          title="Rates changed, who does it apply to?"
          style={{ width: 520 }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <p style={{ margin: 0, fontSize: "var(--font-size-body-medium)", lineHeight: 1.55 }}>
              Every contributor on this project will be notified immediately and must accept the new rates to keep
              working — or stop and be paid what they have already earned. Work frozen into payout runs is never
              touched.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <Button type="button" variant="primary" size="small" waiting={saving} onClick={() => save("future")}>
                Future work only — done work keeps its old rate
              </Button>
              {futureOnly ? (
                <span
                  style={{
                    fontSize: "var(--font-size-body-small)",
                    color: "var(--color-neutral-content-subtler, #64748b)",
                  }}
                >
                  Repricing past work is disabled — set by your organization.
                </span>
              ) : (
                <Button
                  type="button"
                  variant="negative"
                  look="outlined"
                  size="small"
                  waiting={saving}
                  onClick={() => save("all_unpaid")}
                >
                  Also reprice validated unpaid work
                </Button>
              )}
              <Button type="button" look="outlined" size="small" onClick={() => setScopeOpen(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

ContributionsSettings.title = "Contributions";
ContributionsSettings.path = "/contributions";
